//@flow
import * as Immutable from 'immutable';

import EntityTypeEnum from 'common/enums/EntityTypeEnum';
import {
    TemperatureThreshold,
    MasterOrder,
    PreferredQuote,
    LineItem
} from 'common/models/flow/MasterOrder';
import type {
    TemperatureThresholdModelType,
    MasterOrderModelType,
    PreferredQuoteModelType,
    LineItemModelType
} from 'common/models/flow/MasterOrder';

import { AuditMetadata } from 'common/models/internal/AuditMetadata';
import { LocationAddress } from 'common/models/internal/LocationAddress';
import { TeamId } from 'common/models/internal/TeamId';
import { LatLng } from 'common/models/internal/LatLng';
import { Volume } from 'common/models/internal/Volume';
import { Weight } from 'common/models/internal/Weight';
import { PickupContactPoint } from 'common/models/internal/PickupContactPoint';
import { AmountTransaction } from 'common/models/internal/AmountTransaction';
import { TimeSlot } from 'common/models/internal/TimeSlot';
import { Quantity } from 'common/models/internal/Quantity';
import { LineItemPart } from 'common/models/internal/LineItemPart';
import { Amount } from 'common/models/internal/Amount';

class InternalPreferredQuote extends PreferredQuote {
    static fromJS(json: PreferredQuoteModelType): InternalPreferredQuote {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalLineItem extends LineItem {
    static fromJS(json: LineItemModelType): InternalLineItem {
        const state: Object = Object.assign({}, json);
        state.parts = Immutable.List(state.parts).map(item =>
            LineItemPart.fromJS(item)
        );
        state.price = state.price ? Amount.fromJS(state.price) : state.price;
        return new this(Immutable.Map(state));
    }
}

export { InternalLineItem as LineItem };

export const OrderSubStatusEnum = new EntityTypeEnum({
    Open: 'OPEN',
    PendingPlanning: 'PENDING_PLANNING',
    Planning: 'PLANNING',
    Waiting: 'WAITING',
    OnGoing: 'ON_GOING',
    QuoteRequested: 'QUOTE_REQUESTED',
    QuotePendingAcceptance: 'QUOTE_PENDING_ACCEPTANCE',
    QuoteAutoAccepted: 'QUOTE_AUTO_ACCEPTED',
    QuoteAccepted: 'QUOTE_ACCEPTED',
    QuoteRejected: 'QUOTE_REJECTED',
    QuoteExpired: 'QUOTE_EXPIRED',
    QuoteNotFound: 'QUOTE_NOT_FOUND',
    RiderAssigned: 'RIDER_ASSIGNED',
    PickingUp: 'PICKING_UP',
    DroppingOff: 'DROPPING_OFF',
    CarrierRejected: 'CARRIER_REJECTED',
    CustomerUnavailable: 'CUSTOMER_UNAVAILABLE',
    Completed: 'COMPLETED',
    Cancelled: 'CANCELLED'
});

export const OrderStatusEnum = new EntityTypeEnum({
    OPEN: 'OPEN',
    PENDING_PLANNING: 'PENDING_PLANNING',
    PLANNING: 'PLANNING',
    EXECUTION: 'EXECUTION',
    WAITING: 'WAITING',
    ON_GOING: 'ON_GOING',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED'
});

export const TemperatureTypeEnum = new EntityTypeEnum({
    CHILLED: 'CHILLED',
    FROZEN: 'FROZEN'
});

export const TemperatureUnitEnum = new EntityTypeEnum({
    C: 'C',
    F: 'F'
});

export const ExchangeTypeEnum = new EntityTypeEnum({
    COLLECT: 'COLLECT',
    GIVE: 'GIVE',
    NONE: 'NONE'
});

// Used for weightUnit filter options
export const WeightUnitEnum = new EntityTypeEnum({
    KG: 'KG', //Kilogram
    G: 'G', //Gram
    MG: 'MG', //Milligram
    T: 'T', //Ton
    OZ: 'OZ', //Ounce
    LB: 'LB' //Pound
});

// Used for volumeUnit filter options
export const VolumeUnitEnum = new EntityTypeEnum({
    CMM: 'CMM', //Cubic Millimeter
    CCM: 'CCM', //Cubic Centimeter
    CM: 'CM', //Cubic Meter
    CIN: 'CIN', //Cubic Inch
    CFT: 'CFT', //Cubic Feet
    CYD: 'CYD', //Cubic Yard
    IC: 'IC', //Item Count
    TC: 'TC' //Task Count
});

export const FulfilledByEnum = new EntityTypeEnum({
    DISPATCH_IQ: 'DISPATCH_IQ',
    ORCHESTRATION_IQ: 'ORCHESTRATION_IQ',
    TRACK_IQ: 'TRACK_IQ'
});

// Used for totalQuantityUnit filter options
export const QuantityUnitEnum = new EntityTypeEnum({
    PC: 'PC', //Pieces
    CS: 'CS' //Cases
});

class InternalTemperatureThreshold extends TemperatureThreshold {
    static fromJS(
        json: TemperatureThresholdModelType
    ): InternalTemperatureThreshold {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }
}

class InternalMasterOrder extends MasterOrder {
    static fromJS(json: MasterOrderModelType): InternalMasterOrder {
        const state: Object = Object.assign({}, json);
        state.customProperties = Immutable.Map(state.customProperties);
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.team = TeamId.fromJS(state.team);
        state.skills = Immutable.List(state.skills);
        state.temperatureThreshold = state.temperatureThreshold
            ? InternalTemperatureThreshold.fromJS(state.temperatureThreshold)
            : state.temperatureThreshold;
        state.pickupContactPoint = state.pickupContactPoint
            ? PickupContactPoint.fromJS(state.pickupContactPoint)
            : state.pickupContactPoint;
        state.pickupLocationAddress = LocationAddress.fromJS(
            state.pickupLocationAddress
        );
        state.pickupLatLng = LatLng.fromJS(state.pickupLatLng);
        state.pickupSlot = TimeSlot.fromJS(state.pickupSlot);
        state.pickupSlots = Immutable.List(state.pickupSlots).map(item =>
            TimeSlot.fromJS(item)
        );
        state.pickupAmount = AmountTransaction.fromJS(state.pickupAmount);
        state.dropContactPoint = state.dropContactPoint
            ? PickupContactPoint.fromJS(state.dropContactPoint)
            : state.dropContactPoint;
        state.dropLocationAddress = LocationAddress.fromJS(
            state.dropLocationAddress
        );
        state.dropLatLng = LatLng.fromJS(state.dropLatLng);
        state.dropSlot = TimeSlot.fromJS(state.dropSlot);
        state.dropSlots = Immutable.List(state.dropSlots).map(item =>
            TimeSlot.fromJS(item)
        );
        state.dropAmount = AmountTransaction.fromJS(state.dropAmount);
        state.volume = Volume.fromJS(state.volume);
        state.weight = Weight.fromJS(state.weight);
        state.quantity = state.quantity
            ? Quantity.fromJS(state.quantity)
            : state.quantity;
        state.preferredQuote = state.preferredQuote
            ? InternalPreferredQuote.fromJS(state.preferredQuote)
            : state.preferredQuote;
        state.availableQuoteIds = Immutable.List(state.availableQuoteIds);
        state.lineItems = Immutable.List(state.lineItems).map(item =>
            InternalLineItem.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    get team(): TeamId {
        return this._state.get('team');
    }

    get temperatureThreshold(): ?InternalTemperatureThreshold {
        return this._state.get('temperatureThreshold');
    }

    get pickupContactPoint(): ?PickupContactPoint {
        return this._state.get('pickupContactPoint');
    }

    get pickupLocationAddress(): LocationAddress {
        return this._state.get('pickupLocationAddress');
    }

    get pickupLatLng(): LatLng {
        return this._state.get('pickupLatLng');
    }

    get pickupSlot(): TimeSlot {
        return this._state.get('pickupSlot');
    }

    get pickupSlots(): Immutable.List<TimeSlot> {
        return this._state.get('pickupSlots');
    }

    get pickupAmount(): AmountTransaction {
        return this._state.get('pickupAmount');
    }

    get dropContactPoint(): ?PickupContactPoint {
        return this._state.get('dropContactPoint');
    }

    get dropLocationAddress(): LocationAddress {
        return this._state.get('dropLocationAddress');
    }

    get dropLatLng(): LatLng {
        return this._state.get('dropLatLng');
    }

    get dropSlot(): TimeSlot {
        return this._state.get('dropSlot');
    }

    get dropSlots(): Immutable.List<TimeSlot> {
        return this._state.get('dropSlots');
    }

    get dropAmount(): AmountTransaction {
        return this._state.get('dropAmount');
    }

    get volume(): Volume {
        return this._state.get('volume');
    }

    get weight(): Weight {
        return this._state.get('weight');
    }

    get quantity(): ?Quantity {
        return this._state.get('quantity');
    }

    get preferredQuote(): ?InternalPreferredQuote {
        return this._state.get('preferredQuote');
    }
}

export {
    InternalTemperatureThreshold as TemperatureThreshold,
    InternalMasterOrder as MasterOrder,
    InternalPreferredQuote as PreferredQuote
};
